import { useContext, useEffect, useState, useRef } from 'react';
import { AppContext } from '@/context/AppContext';
import { getSummaryStats } from '@/utils/pdfUtils';

const STAT_CARDS = [
  { key: 'top3', label: 'TOP 3', color: '#00C48C', bgClass: 'bg-[#00C48C]/5 border-[#00C48C]/15' },
  { key: 'page1', label: 'PAGE 1 (4-10)', color: '#60A5FA', bgClass: 'bg-[#60A5FA]/5 border-[#60A5FA]/15' },
  { key: 'page2', label: 'PAGE 2+', color: '#F59E0B', bgClass: 'bg-[#F59E0B]/5 border-[#F59E0B]/15' },
  { key: 'notRanked', label: 'NOT RANKED', color: '#EF4444', bgClass: 'bg-[#EF4444]/5 border-[#EF4444]/15' },
];

function AnimatedNumber({ value, color }) {
  const [displayValue, setDisplayValue] = useState(0);
  const rafRef = useRef(null);
  const prevValueRef = useRef(0);

  useEffect(() => {
    const startTime = performance.now();
    const duration = 800;
    const startValue = prevValueRef.current;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startValue + (value - startValue) * easeOut);
      
      setDisplayValue(current);
      
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        prevValueRef.current = value;
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [value]);

  return (
    <span 
      className="text-3xl sm:text-4xl font-black font-mono" 
      style={{ color }}
    >
      {displayValue}
    </span>
  );
}

export default function SummaryStrip() {
  const { rankings, activeClientId } = useContext(AppContext);
  const clientRankings = rankings[activeClientId] || [];
  const stats = getSummaryStats(clientRankings);

  return (
    <div 
      className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
      data-testid="summary-strip"
    >
      {STAT_CARDS.map(card => (
        <div
          key={card.key}
          className={`rounded-xl border p-4 sm:p-5 text-center transition-all hover:scale-[1.02] ${card.bgClass}`}
          data-testid={`stat-card-${card.key}`}
        >
          <AnimatedNumber value={stats[card.key]} color={card.color} />
          <div className="text-[9px] sm:text-[10px] font-mono text-[#4A5568] uppercase tracking-wider mt-1.5 sm:mt-2">
            {card.label}
          </div>
        </div>
      ))}
    </div>
  );
}
